import { CdkConnectedOverlay, CdkOverlayOrigin } from '@angular/cdk/overlay';
import { ChangeDetectorRef, Component, Input, ViewChild } from '@angular/core';
import { AbstractControl, UntypedFormArray, UntypedFormBuilder, Validators } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { getFieldTypeIcon, getTagTypeIcon } from 'app/field-shared/field-helper-functions';

import {
  AddOrUpdateFieldResponseDialog
} from 'app/field-shared/dialogs/add-or-update-field-response/add-or-update-field-response.dialog';
import { WithDisposableSubject } from 'core/mixins/with-disposable-subject';
import { isNilOrEmpty } from 'helpers';

import { SearchTags, TagCategoryInfoFragment, TagType } from 'generated/graphql';

@Component({
  selector: 'app-apply-tag-multi-form',
  templateUrl: './apply-tag-multi-form.component.html'
})
export class ApplyTagMultiFormComponent extends WithDisposableSubject() {

  // <Overlay Related>
  /** Is the select tag dropdown open? */
  isOpen = false;

  /** Reference to trigger to open the select
   *  (Used to dynamically set initial width)
   */
  @ViewChild(CdkOverlayOrigin) trigger: CdkOverlayOrigin;

  @ViewChild(CdkConnectedOverlay) overlay: CdkConnectedOverlay;

  /**
   *  Use observable to get the trigger width to avoid a
   *  ExpressionChangedAfterItHasBeenCheckedError
   */
  private _triggerWidth = new Subject<string>();

  /**
   *  Use observable to get the trigger width to avoid a
   *  ExpressionChangedAfterItHasBeenCheckedError
   */
  triggerWidth$ = this._triggerWidth.asObservable().pipe(
    takeUntil(this.disposer)
  );

  // </Overlay Related>

  /**
   * Disable autofocus
   */
  @Input() autoFocusDisabled = false;

  /**
   * Required input of the tag category
   */
  @Input() tagCategory: TagCategoryInfoFragment;

  /**
   * Required input for campaign id
   */
  @Input() campaignId: string;

  /** form array */
  @Input() formArray: UntypedFormArray;

  /** If true, attachments input is hidden */
  @Input() hideAttachments: boolean;

  /**
   *
   */
  constructor(
    private _fb: UntypedFormBuilder,
    private _cd: ChangeDetectorRef,
    private _dialog: MatDialog
  ) {
    super();
  }

  getMinRole(tagCategory: TagCategoryInfoFragment) {
    return tagCategory.readonly ? 'administrator' : 'activist';
  }

  getTagIcon(tagType) {
    const iconClass = getTagTypeIcon(tagType);
    return iconClass ? iconClass : 'fa-tag';
  }

  openOverlay() {
    const width = this.trigger?.elementRef?.nativeElement?.offsetWidth;
    this._triggerWidth.next(width ? `${width}px` : '100%');
    this.isOpen = true;
    this._cd.markForCheck();
  }

  closeOverlay() {
    this.isOpen = false;
    this._cd.markForCheck();
  }

  onAddNewResponseClick() {
    this.isOpen = false;
    AddOrUpdateFieldResponseDialog.open(this._dialog, {
      data: {
        tagCategoryId: this.tagCategory.id,
        tagType: this.tagCategory.allowToCreateTagType,
        tag: null,
        campaignId: this.campaignId,
        isUniversal: this.tagCategory.isUniversal
      }
    }).afterClosed().subscribe((createdTag) => {
      if (!isNilOrEmpty(createdTag)) {
        this._pushSelectedTag(createdTag);
      }
    });
  }

  onTagSelected(tag: SearchTags.Node) {
    if (this.isTagSelected(tag)) {
      this.removeTag(tag);
    } else {
      this._pushSelectedTag(tag);
    }

    // setTimeout(() => this.overlay.overlayRef.updatePosition());
    this._cd.detectChanges();
    this.formArray.markAsTouched();
  }

  removeTag(selectedTag: { id: string }) {
    this.formArray.removeAt(
      this.formArray.controls.indexOf(this._findTagControl(selectedTag))
    );
    this.formArray.markAsTouched();
    this._cd.markForCheck();
  }

  isTagSelected(response: { id: string }) {
    return this.formArray.value.map(t => t.selectedTag?.id).includes(response?.id);
  }

  onTagCheckboxChanged(event: MatCheckboxChange, response: any) {
    // This is a hack.  The click event handler on the app-response-search item
    // is fired before the tagCheckboxChange event when an item is clicked;
    // This causes a race condition and the checkbox is not correctly updated
    // the checked state.  This might be a bug in the angular checkbox
    event.source.checked = this.isTagSelected(response);
    this._cd.markForCheck();
  }

  setFile(file: File, control: AbstractControl) {
    control.setValue(file);
    this.formArray.markAsTouched();
    this._cd.markForCheck();
  }

  private _pushSelectedTag(selectedTag: { id: string; tagType: TagType; name: string }) {
    // Tag exists, skip
    if (this.isTagSelected(selectedTag)) {
      return;
    }
    const newFg = this._fb.group(
      {
        selectedTag: [selectedTag, Validators.required],
        note: null,
        shiftInfo: null,
        file: null
      }
    );

    this.formArray.push(newFg);
    this._cd.markForCheck();
  }

  private _findTagControl(selectedTag: { id: string }) {
    return this.formArray.controls.find(st => st.value?.selectedTag?.id === selectedTag.id);
  }
}
